import {
  CalendarIcon,
  ClockIcon,
  PersonIcon,
  RocketIcon,
  SewingPinIcon,
  FileTextIcon,
} from "@radix-ui/react-icons";
import {
  MeetingAdminResponse,
  MeetingLimitedResponse,
  SessionFrequency,
  StudentsService,
  ParentLimitedResponse,
  ParentsService,
} from "client/openapi";
import MeetingInfoItem from "./meetingInfoItem";
import { plural } from "components/Classes/classText";
import { TagColor, TagSize } from "components/Tag";
import { Tag } from "components/Tag/TagChip";
import moment from "moment";
import { WEEKDAYS } from "..";
import { useEffect, useState } from "react";
import { parseFrequency } from "util/parseRecurrencePattern";
import { Dialog, DialogTrigger } from "components/Dialog";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import { IoMail } from "react-icons/io5";
import ComposeMessageDialog from "components/Dashboard/ComposeMessageDialog";
import { APIResponse } from "types";
import Notifications from "util/notifications";

const TIME_FORMAT = "h:mm A";

export default function ViewMeetingDetails({
  event,
}: {
  event: MeetingAdminResponse | MeetingLimitedResponse;
}) {
  const [meetingDays, setMeetingDays] = useState<string[]>([]);

  async function getWeekdays() {
    if (!event?.start) {
      return;
    } else {
      const meeting_day = moment(event.start)
        .format("ddd")
        .toUpperCase()
        .substring(0, 2);
      setMeetingDays([meeting_day]);
    }
  }

  useEffect(() => {
    getWeekdays();
  }, [event]);

  return (
    <>
      <div>
        <div className="grid grid-cols-12 p-2 pr-2">
          <div className="pt-1 col-span-1">
            <CalendarIcon />
          </div>
          <div className="col-span-11 sm:col-span-6 pb-4 grid-rows-3 sm:border-b-2 sm:border-b-slate-300 space-y-2">
            <div className="block md:flex">
              <p className="font-bold">
                {moment(event.start).format("ddd, MMM Do, YYYY")},&nbsp;
              </p>
              {moment(event.start).format(TIME_FORMAT)} -{" "}
              {moment(event.start)
                .add(event.duration, "minutes")
                .format(TIME_FORMAT)}
            </div>
            <p>
              {event.session.frequency === SessionFrequency.SINGLE
                ? "does not repeat"
                : `repeats ${parseFrequency(
                    event.session.frequency
                  )} until ${moment(event.session.ends_on_or_before).format(
                    "MM/DD/YY"
                  )}`}
            </p>
          </div>
          <div className="col-span-11 col-start-2 sm:col-span-5 grid-rows-3 border-b-2 border-b-slate-300 space-y-2 pb-4">
            <p className="font-bold">This sequence is on:</p>
            {WEEKDAYS.map((day, index) => {
              return (
                <span key={index} className="mr-2">
                  <Tag
                    item={day}
                    color={
                      meetingDays.includes(day) ? TagColor.GREEN : TagColor.GRAY
                    }
                    size={TagSize.SQUARE}
                  />
                </span>
              );
            })}
          </div>
        </div>
        {(event.other_location || event.location) && (
          <MeetingInfoItem icon={<SewingPinIcon />}>
            <p>{event.other_location || event.location?.name}</p>
          </MeetingInfoItem>
        )}
        <MeetingInfoItem icon={<ClockIcon />}>
          <p>{event.duration} minutes</p>
        </MeetingInfoItem>
        <MeetingInfoItem icon={<RocketIcon />}>
          <p>{plural(event.students.length, "student")}</p>
        </MeetingInfoItem>
        <MeetingInfoItem icon={<PersonIcon />}>
          <p>{plural(event.tutors.length, "tutor")}</p>
        </MeetingInfoItem>
        {event.note && (
          <MeetingInfoItem icon={<FileTextIcon />}>
            <p>{event.note.content}</p>
            <div className="mt-2">
              <LazyMessageDialog meeting={event} />
            </div>
          </MeetingInfoItem>
        )}
      </div>
    </>
  );
}

function LazyMessageDialog({
  meeting,
}: {
  meeting: MeetingAdminResponse | MeetingLimitedResponse;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<APIResponse>();

  const [parents, setParents] = useState<ParentLimitedResponse[]>([]);
  const [parentsLoaded, setParentsLoaded] = useState(false);

  useEffect(() => {
    if (open && meeting.students.length > 0) {
      setLoading(true);
      Promise.all(
        meeting.students.map((meetingStudent) =>
          ParentsService.getParentsByStudent({
            studentId: meetingStudent.student.id,
          })
        )
      )
        .then((parentsResponses) => {
          const allParents = parentsResponses.flat();
          const uniqueParents = allParents.filter(
            (parent, index, self) =>
              index === self.findIndex((p) => p.id === parent.id)
          );
          setParents(uniqueParents);
          setParentsLoaded(true);
          setLoading(false);
        })
        .catch((e) => {
          setError({ error: "An unexpected error occurred" });
          setLoading(false);
          console.error(`Error: ${e.message}`);
        });
    }
  }, [open, meeting]);

  useEffect(() => {
    error &&
      Notifications.error(error?.message || "An unexpected error occurred.");
  }, [error]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          size={ButtonSize.EXTRA_SMALL}
          color={ButtonColor.DARK_BLUE}
          fill={ButtonFill.HOLLOW}
          disabled={loading}
        >
          {loading ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            ></span>
          ) : (
            <IoMail />
          )}
        </Button>
      </DialogTrigger>
      {open && (
        <>
          {loading || !parentsLoaded ? (
            <div></div>
          ) : (
            <ComposeMessageDialog
              students={meeting.students.map(
                (meetingStudent) => meetingStudent.student
              )}
              parents={parents}
              org_id={meeting.org_id}
              preloadedContent={meeting.note?.content}
            />
          )}
        </>
      )}
    </Dialog>
  );
}
